import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { CV_CAPABILITIES, CV_REPORTS, IMAGES } from "@/data/content";

export default function CabinetVision() {
  return (
    <div data-testid="page-cabinet-vision">
      <PageHero
        overline="— Service / 03"
        title="Cabinet Vision &"
        accent="production systems."
        body="Connected systems where information created at the front end remains useful for pricing, production drawings, bills of materials, hardware, door ordering, CNC manufacturing and installation."
        image={IMAGES.cabinetry}
        testid="cv-hero"
      />

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-12 gap-12">
        <Reveal className="md:col-span-5">
          <div className="overline mb-3">System development</div>
          <h2 className="font-serif text-4xl leading-tight">A library is the foundation everything else stands on.</h2>
        </Reveal>
        <Reveal className="md:col-span-6 md:col-start-7 text-[#A3A3A3] text-lg leading-relaxed" delay={120}>
          <p>
            Services include cabinet catalogue development, library organization, construction
            methods, material schedules, door and drawer standards, hardware standards, part naming,
            assemblies, attributes, parameters, drawing templates, annotation systems, layers,
            material callouts and company-specific defaults.
          </p>
          <p className="mt-4 text-[#EAEAEA]">
            A structured Cabinet Vision system reduces the need to rebuild the same cabinet
            repeatedly and improves consistency between users and departments.
          </p>
        </Reveal>
      </section>

      <section className="bg-[#0E0E0E] border-y border-white/5 py-24" data-testid="cv-capabilities">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="overline mb-3">— Parametric capabilities</div>
            <h2 className="font-serif text-4xl mb-6 leading-tight">Cabinets that behave intelligently.</h2>
            <p className="text-[#A3A3A3] leading-relaxed">
              Parametric cabinets and intelligent assemblies that respond to dimensions,
              construction, materials, hardware and selected conditions.
            </p>
          </Reveal>
          <ul className="md:col-span-7 grid sm:grid-cols-2 gap-x-8">
            {CV_CAPABILITIES.map((c, i) => (
              <Reveal key={c} delay={i * 25}>
                <li className="py-3 border-b border-white/5 flex gap-4 text-[15px]">
                  <span className="text-[#B58B5D]">●</span>
                  <span className="text-[#EAEAEA]">{c}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24" data-testid="cv-ucs">
        <Reveal>
          <div className="overline mb-3">— UCS programming</div>
          <h2 className="font-serif text-4xl md:text-5xl mb-12 max-w-3xl">Rule-based automation that converts company knowledge into repeatable logic.</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-12">
          <Reveal>
            <h3 className="font-serif text-2xl text-[#B58B5D] mb-4">UCS handles</h3>
            <ul className="space-y-3 text-[#EAEAEA] text-[15px]">
              {[
                "Add or remove parts automatically",
                "Change part dimensions and material assignments",
                "Apply edge banding and finished-end conditions",
                "Add hardware, hinges, plates, drawer systems and supports",
                "Create drilling, routing and machining operations",
                "Control reveals, scribes, toe kicks and moulding conditions",
                "Create part descriptions, production notes and custom attributes",
              ].map((u) => (
                <li key={u} className="border-b border-white/5 py-3 flex gap-3"><span className="text-[#B58B5D]">—</span>{u}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={120}>
            <div className="dl-card p-8 h-full">
              <div className="overline mb-4">Example logic</div>
              <p className="font-serif text-2xl text-[#EAEAEA] leading-relaxed">
                <em className="text-[#B58B5D] not-italic">If</em> a cabinet exceeds a defined width,
                add a centre support. <em className="text-[#B58B5D] not-italic">If</em> an end is
                exposed, change the material, construction, edge treatment and drawing note.{" "}
                <em className="text-[#B58B5D] not-italic">If</em> a drawer opening falls within a
                defined range, assign the correct drawer system and hardware code.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#0E0E0E] border-y border-white/5 py-24" data-testid="cv-reports">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="overline mb-3">— Custom reports</div>
            <h2 className="font-serif text-4xl leading-tight">Each department gets the information it needs.</h2>
          </Reveal>
          <ul className="md:col-span-7 grid sm:grid-cols-2 gap-x-8">
            {CV_REPORTS.map((r, i) => (
              <Reveal key={r} delay={i * 25}>
                <li className="py-3 border-b border-white/5 flex gap-3 text-[15px]">
                  <span className="num text-[#B58B5D]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-[#EAEAEA]">{r}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 text-center">
        <Reveal>
          <h2 className="font-serif text-4xl md:text-5xl mb-6">Start a <em className="text-[#B58B5D] not-italic">Cabinet Vision project.</em></h2>
          <p className="text-[#A3A3A3] max-w-xl mx-auto mb-8">Library development, UCS programming, custom reports or a full workflow refresh &mdash; let&apos;s talk through your current setup.</p>
          <Link to="/contact" className="dl-btn-primary">Discuss your workflow</Link>
        </Reveal>
      </section>
    </div>
  );
}
